import { NavLink, useMatch } from 'react-router-dom';
import {
  IconAgent,
  IconArrowsRepeat,
  IconBell,
  IconBook,
  IconBooks,
  IconChevronLeft,
  IconComments,
  IconDatabase,
  IconFolderBookmark,
  IconGear,
  IconPuzzlePiece,
  IconScrewdriverWrench,
  IconStore,
  IconUserRobot,
} from '@optiaxiom/icons';
import {
  Nav,
  NavBody,
  NavFooter,
  NavGroup,
  NavGroupContent,
  NavGroupTrigger,
  NavItem,
  NavList,
  NavSeparator,
  Sidebar,
  SidebarToggle,
} from '@optiaxiom/react';

import { NavigationItem } from '@frontend/components/ui/NavigationItem';
import { FeatureFlags } from '@frontend/constants/featureFlags';
import { useBadgeCount } from '@frontend/features/notifications/hooks';
import { useLocalStorage, useOperations, useUnreadThread } from '@frontend/hooks';
import { InstanceOperationsName, OperationsName } from '@frontend/types';
import { isDesktopApp } from '@frontend/utils/desktop';

import { useVAUContext } from '../../vau/hooks/useVAUContext';

import { OpalOrbIcon } from './OpalOrbIcon';
import { VAUSwitcher } from './VAUSwitcher';

type RouteSection = 'top' | 'config' | 'footer';

type Route = {
  path: string;
  icon: JSX.Element;
  label: string;
  permissions: OperationsName[];
  instanceCapabilities?: InstanceOperationsName[];
  featureFlag?: string;
  section: RouteSection;
  /**
   * Hide this entry while the caller is acting as a VAU. The VAU
   * itself shouldn't see the VAU management surface — the API still
   * works for the principal-side switcher, but the page is hidden.
   */
  hideInVauMode?: boolean;
};

export const LeftNavbar = () => {
  const { canUser, canInstance } = useOperations();
  const [sidebarExpanded, setSidebarExpanded] = useLocalStorage('sidebar-expanded', false);
  const { hasUnreadThreads } = useUnreadThread();
  // Determine if the current route is the chat route
  const isOnChatRoute = useMatch('/instances/:instanceId/chat');

  // Used to hide VAU-management entries when the caller is acting as a VAU.
  const { data: vauContext } = useVAUContext();
  const isInVauMode = vauContext?.is_assumed === true;

  const { data: unreadNotificationCount } = useBadgeCount({ grouped: true });

  const routes: Route[] = [
    {
      path: 'chat',
      icon: <OpalOrbIcon />,
      label: 'Chat',
      permissions: ['use_chat'],
      section: 'top',
    },
    {
      path: 'messages',
      icon: <IconComments />,
      label: 'Team Messages',
      permissions: ['use_chat'],
      featureFlag: FeatureFlags.MESSAGING_ENABLED,
      section: 'top',
    },
    {
      path: 'artifacts',
      icon: <IconBooks />,
      label: 'Artifacts',
      permissions: ['use_chat'],
      section: 'top',
    },
    {
      path: 'notifications',
      icon: <IconBell />,
      label: 'Notifications',
      permissions: ['use_chat'], // TODO: support empty permissions array for truly public pages
      section: 'top',
    },
    {
      path: 'context',
      icon: <IconFolderBookmark />,
      label: 'Context',
      // We should add view_context or similar permission
      permissions: ['view_instructions', 'manage_instructions', 'use_chat', 'manage_rag_config'],
      section: 'config',
    },
    {
      path: 'instructions',
      icon: <IconBook />,
      label: 'Instructions',
      permissions: ['view_instructions', 'manage_instructions', 'use_chat'],
      section: 'config',
    },
    {
      path: 'agents',
      icon: <IconAgent />,
      label: 'Agents',
      permissions: ['view_agents'],
      section: 'config',
    },
    {
      path: 'vaus',
      icon: <IconUserRobot />,
      label: 'Virtual Agentic Users',
      // VAU management piggybacks on the existing agent-view permission;
      // gated by the `vau` feature flag (C4). Hidden while the caller
      // is acting as a VAU — VAUs don't manage other VAUs.
      // TODO: re-add instanceCapabilities: ['vau_enabled'] once provisioned in Turnstile.
      permissions: ['view_agents'],
      featureFlag: 'vau',
      section: 'config',
      hideInVauMode: true,
    },
    {
      path: 'tools',
      icon: <IconScrewdriverWrench />,
      label: 'Tools',
      permissions: ['view_tools', 'use_chat'],
      section: 'config',
    },
    {
      path: 'rag',
      icon: <IconDatabase />,
      label: 'RAG',
      permissions: ['manage_rag_config'],
      instanceCapabilities: ['RAG_enabled'],
      section: 'config',
    },
    {
      path: 'plugins',
      icon: <IconPuzzlePiece />,
      label: 'Plugins',
      permissions: ['use_chat'],
      featureFlag: 'plugins',
      section: 'config',
    },
    {
      path: 'directory',
      icon: <IconStore />,
      label: 'Directory',
      permissions: ['use_chat'],
      featureFlag: FeatureFlags.DIRECTORY,
      section: 'config',
    },
    {
      path: 'settings',
      icon: <IconGear />,
      label: 'Settings',
      permissions: ['use_chat'],
      section: 'config',
    },
  ];

  const shouldShowIndicator = (route: Route) => {
    if (route.path === 'chat') return hasUnreadThreads && !isOnChatRoute;
    if (route.path === 'notifications') return (unreadNotificationCount ?? 0) > 0;
    return false;
  };

  const renderNavItem = (route: Route) => {
    return (
      <NavigationItem
        key={route.path}
        path={route.path}
        icon={route.icon}
        label={route.label}
        permissions={route.permissions}
        instanceCapabilities={route.instanceCapabilities}
        featureFlag={route.featureFlag}
        showIndicator={shouldShowIndicator(route)}
      />
    );
  };

  const renderNavItems = ({ section }: { section: RouteSection }) =>
    routes
      .filter(route => {
        if (route.section !== section) return false;

        // Hide VAU-management entries while the caller is acting as a VAU.
        if (route.hideInVauMode && isInVauMode) return false;

        // Check permission
        if (!canUser(route.permissions)) return false;

        // Check instance capabilities
        if (route.instanceCapabilities && !canInstance(route.instanceCapabilities)) return false;

        // RAG & Instructions moved under Context.
        if (route.path === 'rag' || route.path === 'instructions') return false;

        return true;
      })
      .map(renderNavItem);

  const configItems = renderNavItems({ section: 'config' });

  return (
    <Sidebar defaultExpanded={sidebarExpanded} onExpandedChange={setSidebarExpanded}>
      <Nav>
        <NavBody>
          <NavList>{renderNavItems({ section: 'top' })}</NavList>
          {configItems.length > 0 && (
            <NavGroup>
              <NavSeparator />
              {sidebarExpanded && <NavGroupTrigger>Configurations</NavGroupTrigger>}
              <NavGroupContent>
                <NavList>{configItems}</NavList>
              </NavGroupContent>
            </NavGroup>
          )}
        </NavBody>

        <NavFooter>
          <NavList>
            <VAUSwitcher />
            {isDesktopApp() && (
              <NavLink to="/instances" style={{ textDecoration: 'none', color: 'inherit' }}>
                <NavItem icon={<IconArrowsRepeat />}>Switch Instance</NavItem>
              </NavLink>
            )}
            <SidebarToggle icon={<IconChevronLeft />} />
            {renderNavItems({ section: 'footer' })}
          </NavList>
        </NavFooter>
      </Nav>
    </Sidebar>
  );
};
